/**
 * User Settings Types and Utilities
 */

export type DateFormatType = 'Iso8601' | 'European' | 'Us';
export type TimeFormatType = 'Hour24' | 'Hour12';

/**
 * Valid flat-list column keys (Spec 019, E2), in fixed display order.
 * "name" is always visible regardless of `listColumns` and cannot be hidden.
 */
export const ALL_LIST_COLUMN_KEYS = [
  'name',
  'subject',
  'format',
  'size',
  'referenceDate',
  'uploadedAt',
  'category',
  'assignedTo',
] as const;

export type ListColumnKey = (typeof ALL_LIST_COLUMN_KEYS)[number];

/** i18n label key per column, reusing DocumentTable's existing column_* keys. */
export const LIST_COLUMN_LABEL_KEYS: Record<ListColumnKey, string> = {
  name: 'components.document_table.column_name',
  subject: 'components.document_table.column_subject',
  format: 'components.document_table.column_format',
  size: 'components.document_table.column_size',
  referenceDate: 'components.document_table.column_date_reference',
  uploadedAt: 'components.document_table.column_date_uploaded',
  category: 'components.document_table.column_category_combined',
  assignedTo: 'components.document_table.column_assigned_to',
};

export interface UserSettings {
  locale: string;
  dateFormat: DateFormatType;
  timeFormat: TimeFormatType;
  allowedIngestEmails: string[];
  listColumns?: string[];
}

export interface UserProfile {
  id: string;
  email: string;
  displayName: string;
  pictureUrl: string | null;
  role: string;
  settings: UserSettings;
  createdAt: string;
  lastLoginAt: string | null;
}

/**
 * Default settings (en-US, ISO8601, 24h)
 */
export const defaultSettings: UserSettings = {
  locale: 'en-US',
  dateFormat: 'Iso8601',
  timeFormat: 'Hour24',
  allowedIngestEmails: [],
  listColumns: [...ALL_LIST_COLUMN_KEYS],
};

/**
 * Detect browser settings for initial defaults
 */
export function detectBrowserSettings(): Partial<UserSettings> {
  const locale = navigator.language || 'en-US';
  const timeFormat: TimeFormatType = locale.startsWith('en-US') ? 'Hour12' : 'Hour24';

  return {
    locale,
    timeFormat,
  };
}

/**
 * Format a date/time string using user settings
 */
export function formatDateTime(dateStr: string, settings: UserSettings): string {
  const datePart = formatDate(dateStr, settings);
  const timePart = formatTime(dateStr, settings);
  return `${datePart}, ${timePart}`;
}

/**
 * Format a date string (without time) using user settings
 */
export function formatDate(dateStr: string, settings: UserSettings): string {
  const date = new Date(dateStr);

  // ISO 8601 needs manual formatting: YYYY-MM-DD
  if (settings.dateFormat === 'Iso8601') {
    return formatIso8601Date(date);
  }

  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  };

  // Use specific locale for consistent formatting
  // European: DD.MM.YYYY (de-DE), US: MM/DD/YYYY (en-US)
  const locale = settings.dateFormat === 'European' ? 'de-DE' : 'en-US';
  return new Intl.DateTimeFormat(locale, options).format(date);
}

/**
 * Format a time string using user settings
 */
export function formatTime(dateStr: string, settings: UserSettings): string {
  const date = new Date(dateStr);

  // For 24h format, manually format to ensure consistency
  if (settings.timeFormat === 'Hour24') {
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  }

  // For 12h format, use Intl with explicit hour12: true
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  return new Intl.DateTimeFormat(settings.locale, options).format(date);
}

/**
 * Format date as ISO 8601: YYYY-MM-DD
 */
function formatIso8601Date(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get display label for date format
 */
export function getDateFormatLabel(format: DateFormatType): string {
  switch (format) {
    case 'Iso8601':
      return 'ISO 8601 (2025-01-15)';
    case 'European':
      return 'European (15.01.2025)';
    case 'Us':
      return 'US (01/15/2025)';
  }
}

/**
 * Get display label for time format
 */
export function getTimeFormatLabel(format: TimeFormatType): string {
  switch (format) {
    case 'Hour24':
      return '24 Hour (14:30)';
    case 'Hour12':
      return '12 Hour (2:30 PM)';
  }
}

/**
 * Get display label for locale
 */
export function getLocaleLabel(locale: string): string {
  const labels: Record<string, string> = {
    'de-DE': 'Deutsch (Deutschland)',
    'en-US': 'English (US)',
    'en-GB': 'English (UK)',
    'fr-FR': 'Fran\u00e7ais',
    'es-ES': 'Espa\u00f1ol',
    'it-IT': 'Italiano',
  };
  return labels[locale] || locale;
}

/**
 * Available locales
 */
export const availableLocales = [
  { value: 'de-DE', label: 'Deutsch (Deutschland)' },
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'fr-FR', label: 'Fran\u00e7ais' },
  { value: 'es-ES', label: 'Espa\u00f1ol' },
  { value: 'it-IT', label: 'Italiano' },
];

/**
 * Available date formats
 */
export const availableDateFormats: { value: DateFormatType; label: string }[] = [
  { value: 'Iso8601', label: 'ISO 8601 (2025-01-15)' },
  { value: 'European', label: 'European (15.01.2025)' },
  { value: 'Us', label: 'US (01/15/2025)' },
];

/**
 * Available time formats
 */
export const availableTimeFormats: { value: TimeFormatType; label: string }[] = [
  { value: 'Hour24', label: '24 Hour (14:30)' },
  { value: 'Hour12', label: '12 Hour (2:30 PM)' },
];
